import { Calendar, ChevronDown, RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import { EVENT_TYPE_OPTIONS, RANGES, SOURCE_OPTIONS } from "@/pages/admin/data";

interface Chip {
  label: string;
  onRemove: () => void;
}

interface Option {
  value: string;
  label: string;
}

interface LogsFilterBarProps {
  search: string;
  onSearch: (v: string) => void;
  range: string;
  onRange: (v: string) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  retailer: string;
  onRetailer: (v: string) => void;
  retailerOptions?: Option[];
  eventType: string;
  onEventType: (v: string) => void;
  source: string;
  onSource: (v: string) => void;
  onReset: () => void;
  chips: Chip[];
}

export default function LogsFilterBar({
  search,
  onSearch,
  range,
  onRange,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
  retailer,
  onRetailer,
  retailerOptions = [],
  eventType,
  onEventType,
  source,
  onSource,
  onReset,
  chips,
}: LogsFilterBarProps) {
  const [rangeOpen, setRangeOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [tempFrom, setTempFrom] = useState(customFrom);
  const [tempTo, setTempTo] = useState(customTo);

  const displayRangeLabel =
    range === "Custom Range" && (customFrom || customTo)
      ? `${customFrom || "..."} to ${customTo || "..."}`
      : range;

  const handleSelectRange = (r: string) => {
    setRangeOpen(false);
    if (r === "Custom Range") {
      setTempFrom(customFrom);
      setTempTo(customTo);
      setCustomModalOpen(true);
    } else {
      onRange(r);
    }
  };

  const handleApplyCustomRange = () => {
    onCustomFrom(tempFrom);
    onCustomTo(tempTo);
    onRange("Custom Range");
    setCustomModalOpen(false);
  };

  const rangeButton = (
    <button
      type="button"
      onClick={() => {
        if (range === "Custom Range") {
          setTempFrom(customFrom);
          setTempTo(customTo);
          setCustomModalOpen(true);
        } else {
          setRangeOpen((v) => !v);
        }
      }}
      className="inline-flex h-[42px] w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-medium text-ink hover:bg-slate-50 xl:w-auto cursor-pointer"
    >
      <span className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-ink-muted" />
        {displayRangeLabel}
      </span>
      <ChevronDown className="h-4 w-4 text-ink-muted" />
    </button>
  );

  const rangeMenu = rangeOpen && (
    <div className="absolute left-0 top-full z-30 mt-2 w-56 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
      {RANGES.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => handleSelectRange(r)}
          className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
            range === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
          }`}
        >
          {r}
        </button>
      ))}
    </div>
  );

  const retailerSelect = (
    <Select
      value={retailer}
      onChange={(e) => onRetailer(e.target.value)}
      options={retailerOptions}
      placeholder="All Retailers"
      className="xl:w-48"
    />
  );

  const eventSelect = (
    <Select
      value={eventType}
      onChange={(e) => onEventType(e.target.value)}
      options={EVENT_TYPE_OPTIONS}
      placeholder="All Events"
      className="xl:w-44"
    />
  );

  const sourceSelect = (
    <Select
      value={source}
      onChange={(e) => onSource(e.target.value)}
      options={SOURCE_OPTIONS}
      placeholder="All Sources"
      className="xl:w-40"
    />
  );

  return (
    <div className="rounded-[10px] border border-line bg-white p-4 shadow-card">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
        <div className="flex flex-1 items-center gap-3">
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            prefixIcon={<Search className="h-4 w-4" />}
            placeholder="Search by ref no, retailer or event"
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={() => setFiltersOpen(true)}
            leftIcon={<SlidersHorizontal className="h-4 w-4" />}
            className="xl:hidden"
          >
            Filters
          </Button>
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          {retailerSelect}
          {eventSelect}
          {sourceSelect}
          <div className="relative">
            {rangeButton}
            {rangeMenu}
          </div>
          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Reset
          </Button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {chips.map((c, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600"
            >
              {c.label}
              <button
                type="button"
                onClick={c.onRemove}
                className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary-100 cursor-pointer"
                aria-label="Remove filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Mobile Filters Modal */}
      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters">
        <div className="space-y-4">
          <div>{retailerSelect}</div>
          <div>{eventSelect}</div>
          <div>{sourceSelect}</div>
          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Date Range</span>
            <Select
              value={range}
              onChange={(e) => onRange(e.target.value)}
              options={RANGES.map((r) => ({ value: r, label: r }))}
            />
            {range === "Custom Range" && (
              <div className="mt-3 space-y-3">
                <DatePicker value={customFrom} onChange={onCustomFrom} placeholder="From date" />
                <DatePicker value={customTo} onChange={onCustomTo} placeholder="To date" />
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              onReset();
              setFiltersOpen(false);
            }}
            leftIcon={<RotateCcw className="h-4 w-4" />}
          >
            Reset Filters
          </Button>
        </div>
      </Modal>

      {/* Custom Date Range Modal for Desktop */}
      <Modal open={customModalOpen} onClose={() => setCustomModalOpen(false)} title="Select Custom Date Range">
        <div className="space-y-4">
          <p className="text-sm text-ink-secondary">Filter audit logs within a specific start and end date range.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-secondary">From Date</label>
              <input
                type="date"
                value={tempFrom}
                onChange={(e) => setTempFrom(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-ink focus:border-primary-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-secondary">To Date</label>
              <input
                type="date"
                value={tempTo}
                onChange={(e) => setTempTo(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-ink focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setCustomModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApplyCustomRange}>
              Apply Range
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}